/**
 * <variants-slider> — horizontal slider for the "other colors" swatches on a product card.
 *
 * Arrow visibility is derived from real measurements (scrollWidth vs clientWidth) instead of a
 * hardcoded variant count, so it stays correct for any card width / column layout. Native
 * overflow scrolling keeps swipe working on touch devices.
 */
if (!customElements.get("variants-slider")) {
  class VariantsSlider extends HTMLElement {
    connectedCallback() {
      if (this.__initialized) return;
      this.__initialized = true;

      // The element upgrades as soon as its start tag is parsed, so its children may not exist
      // yet. Wait for the next frame, and for DOMContentLoaded if that still isn't enough.
      requestAnimationFrame(() => {
        if (this.init()) return;
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", () => this.init(), {
            once: true,
          });
        }
      });
    }

    disconnectedCallback() {
      this.resizeObserver && this.resizeObserver.disconnect();
    }

    init() {
      if (!this.isConnected || this.__ready) return false;

      this.slider = this.querySelector(".m-product-card__variants");
      this.prevBtn = this.querySelector(".m-product-card__variant-nav.prev");
      this.nextBtn = this.querySelector(".m-product-card__variant-nav.next");
      if (!this.slider || !this.prevBtn || !this.nextBtn) return false;
      this.__ready = true;

      this.prevBtn.addEventListener("click", () => this.slide(-1));
      this.nextBtn.addEventListener("click", () => this.slide(1));
      this.slider.addEventListener("scroll", () => this.updateNav(), {
        passive: true,
      });

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateNav());
        this.resizeObserver.observe(this.slider);
      } else {
        window.addEventListener("resize", () => this.updateNav());
      }

      // Swatch images load lazily; their size decides whether we overflow.
      this.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", () => this.updateNav());
      });

      this.updateNav();
      return true;
    }

    /** Width of one swatch including the gap between swatches. */
    get stepWidth() {
      const item = this.slider.querySelector(".m-product-card__variant");
      if (!item) return 0;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    /** Scroll by as many whole swatches as currently fit, at least one. */
    slide(direction) {
      const step = this.stepWidth;
      if (!step) return;

      const perPage = Math.max(1, Math.floor(this.slider.clientWidth / step));
      this.slider.scrollBy({
        left: direction * step * perPage,
        behavior: "smooth",
      });
    }

    updateNav() {
      if (!this.slider) return;

      // Sub-pixel layout rounding can leave a 1px "overflow" that is not scrollable.
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const scrollable = maxScroll > 1;
      const left = this.slider.scrollLeft;

      this.toggleAttribute("data-scrollable", scrollable);
      this.prevBtn.hidden = !scrollable;
      this.nextBtn.hidden = !scrollable;
      this.prevBtn.disabled = left <= 1;
      this.nextBtn.disabled = left >= maxScroll - 1;
    }
  }

  customElements.define("variants-slider", VariantsSlider);
}
